import { components } from "@/api-schema";
import { useNumberFormatLocale } from "@/common/helpers/number-format";
import { BrandLogo } from "@/components/brand-logo/brand-logo";
import { EmptyValue } from "@/components/ui/empty-value";
import { useTranslation } from "react-i18next";

interface VehicleSummaryCardProps {
  vehicle: components["schemas"]["Vehicle"];
}

export function VehicleSummaryCard({ vehicle }: VehicleSummaryCardProps) {
  const { t } = useTranslation(["fit", "common"]);
  const numberFormatLocale = useNumberFormatLocale();
  const brand = vehicle?.modelGeneration?.modelFamily?.brand;
  const brandName = brand?.name;
  const modelGenerationName = vehicle?.modelGeneration?.name;

  return (
    <div className="flex items-center gap-4 rounded-lg border p-4">
      <div className="shrink-0">{brand && <BrandLogo brand={brand} />}</div>
      <div className="min-w-0 flex-1">
        {(brandName || modelGenerationName) && (
          <div className="text-base font-semibold">{[brandName, modelGenerationName].filter(Boolean).join(" ")}</div>
        )}
        <div className="text-muted-foreground mt-1 space-y-0.5 text-sm">
          <div className="font-mono text-xs">{vehicle.vin}</div>
          <div>
            {t("fit:Mileage")}:{" "}
            {vehicle.mileage != null && Number.isFinite(vehicle.mileage) ? (
              `${new Intl.NumberFormat(numberFormatLocale, { maximumFractionDigits: 0 }).format(vehicle.mileage)} ${t("common:km")}`
            ) : (
              <EmptyValue />
            )}
          </div>
          <div>
            {t("common:OwningBranch")}: {vehicle.owningBranch?.name ?? <EmptyValue />}
          </div>
          <div>
            {t("fit:CurrentBranch")}: {vehicle.currentBranch?.name ?? <EmptyValue />}
          </div>
          {vehicle.color && (
            <div className="flex items-center gap-2">
              <span>{t("common:Color")}:</span>
              <span className="font-medium">{vehicle.color}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
